import React, { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import NotificationPreferences from './NotificationPreferences';
import { useAuth } from '../context/AuthContext';
import { useUISettings } from '../context/UISettingsContext';
import '../styles/Settings.css';

const Settings = () => {
  const { user, login } = useAuth();
  const { iconMode, setIconMode } = useUISettings();
  const [activeTab, setActiveTab] = useState('account');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    phone_number: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const canManageNotificationPreferences = [1, 2].includes(Number(user?.role_id));

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;

      setLoadingProfile(true);
      try {
        const response = await apiClient.get(`${API_BASE_URL}/users/${user.id}`);
        const data = response.data || {};
        setProfileForm({
          username: data.username || '',
          email: data.email || '',
          phone_number: data.phone_number || '',
        });
        setError('');
      } catch {
        setProfileForm({
          username: user?.username || '',
          email: user?.email || '',
          phone_number: user?.phone_number || '',
        });
        setError('Profil bilgileri yüklenemedi.');
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();

    if (!profileForm.username.trim()) {
      setError('Kullanıcı adı boş olamaz.');
      setSuccess('');
      return;
    }

    setSavingProfile(true);
    try {
      const response = await apiClient.put(`${API_BASE_URL}/users/${user.id}`, {
        username: profileForm.username.trim(),
        email: profileForm.email.trim(),
        phone_number: profileForm.phone_number.trim(),
      });

      const updated = response.data || {};
      const token = localStorage.getItem('token');
      login(token, {
        ...user,
        username: updated.username || profileForm.username.trim(),
        email: updated.email || profileForm.email.trim(),
        role_id: updated.role_id || user.role_id,
        job_title: updated.job_title || user.job_title,
        phone_number: updated.phone_number || profileForm.phone_number.trim(),
      });

      setSuccess('Profil bilgileri güncellendi.');
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Profil güncellenemedi.');
      setSuccess('');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();

    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setError('Lütfen tüm şifre alanlarını doldurun.');
      setSuccess('');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setError('Yeni şifre en az 8 karakter olmalı.');
      setSuccess('');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Yeni şifre ve tekrar alanı aynı olmalı.');
      setSuccess('');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await apiClient.post(`${API_BASE_URL}/auth/change-password`, passwordForm);
      login(response.data.token, response.data.user);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setSuccess('Şifre başarıyla güncellendi.');
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Şifre güncellenemedi.');
      setSuccess('');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Ayarlar</h1>
        <p>Hesap, görünüm ve bildirim ayarlarını buradan yönetin.</p>
      </div>

      {error && <div className="settings-alert error">{error}</div>}
      {success && <div className="settings-alert success">{success}</div>}

      <div className="settings-tabs">
        <button
          type="button"
          className={activeTab === 'account' ? 'active' : ''}
          onClick={() => setActiveTab('account')}
        >
          Hesap Bilgileri
        </button>
        <button
          type="button"
          className={activeTab === 'password' ? 'active' : ''}
          onClick={() => setActiveTab('password')}
        >
          Şifre Değiştir
        </button>
        <button
          type="button"
          className={activeTab === 'appearance' ? 'active' : ''}
          onClick={() => setActiveTab('appearance')}
        >
          Görünüm
        </button>
        <button
          type="button"
          className={activeTab === 'notifications' ? 'active' : ''}
          onClick={() => setActiveTab('notifications')}
        >
          Bildirim Tercihleri
        </button>
      </div>

      {activeTab === 'account' && (
        <section className="settings-card">
          <h2>Hesap Bilgileri</h2>
          {loadingProfile ? (
            <div className="settings-muted">Yükleniyor...</div>
          ) : (
            <form className="settings-form" onSubmit={saveProfile}>
              <div className="settings-field">
                <label htmlFor="username">Kullanıcı Adı</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={profileForm.username}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className="settings-field">
                <label htmlFor="email">E-posta</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={profileForm.email}
                  onChange={handleProfileChange}
                />
              </div>
              <div className="settings-field">
                <label htmlFor="phone_number">Telefon</label>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="text"
                  value={profileForm.phone_number}
                  onChange={handleProfileChange}
                  placeholder="+90..."
                />
              </div>
              <button type="submit" className="settings-btn primary" disabled={savingProfile}>
                {savingProfile ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
              </button>
            </form>
          )}
        </section>
      )}

      {activeTab === 'password' && (
        <section className="settings-card">
          <h2>Şifre Değiştir</h2>
          <form className="settings-form" onSubmit={savePassword}>
            <div className="settings-field">
              <label htmlFor="current_password">Mevcut Şifre</label>
              <input
                id="current_password"
                name="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="settings-field">
              <label htmlFor="new_password">Yeni Şifre</label>
              <input
                id="new_password"
                name="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="settings-field">
              <label htmlFor="confirm_password">Yeni Şifre (Tekrar)</label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <button type="submit" className="settings-btn primary" disabled={savingPassword}>
              {savingPassword ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </button>
          </form>
        </section>
      )}

      {activeTab === 'appearance' && (
        <section className="settings-card">
          <h2>Görünüm</h2>
          <div className="settings-field">
            <label>Menü ve pano simge stili</label>
            <div className="settings-toggle-group">
              <button
                type="button"
                className={`settings-btn ${iconMode === 'svg' ? 'primary' : 'secondary'}`}
                onClick={() => setIconMode('svg')}
              >
                Modern Simgeler
              </button>
              <button
                type="button"
                className={`settings-btn ${iconMode === 'emoji' ? 'primary' : 'secondary'}`}
                onClick={() => setIconMode('emoji')}
              >
                Emoji Modu
              </button>
            </div>
            <small className="settings-muted">
              Bu seçenek menüdeki ve panodaki simge görünümünü anında değiştirir.
            </small>
          </div>
        </section>
      )}

      {activeTab === 'notifications' && (
        <section className="settings-card">
          <h2>Bildirim Tercihleri</h2>
          {canManageNotificationPreferences ? (
            <NotificationPreferences embedded />
          ) : (
            <p className="settings-muted">Bu bölüm sadece Admin ve Müdür rollerine açıktır.</p>
          )}
        </section>
      )}
    </div>
  );
};

export default Settings;
